
import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { AlertCircle, ArrowUpDown, LineChart, RefreshCw } from "lucide-react";
import { useFetchTopAds } from "./useFetchTopAds";
import { getPerformanceTrend } from "./adUtils";
import { AdItem } from "./types";

export const TopAdsDropdown: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { topAds, isLoading, error, refreshTopAds } = useFetchTopAds();

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    refreshTopAds();
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-medium">Top-Werbeanzeigen</h3>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost"
            size="sm" 
            onClick={handleRefresh} 
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="sr-only">Aktualisieren</span>
          </Button>
          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-2"
                disabled={isLoading || topAds.length === 0}
              >
                <LineChart className="h-4 w-4" />
                <span className="hidden sm:inline">Top-Anzeigen anzeigen</span>
                <ArrowUpDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[350px] max-h-[500px] overflow-y-auto">
              <DropdownMenuLabel className="font-normal text-center">
                <span className="block text-sm font-semibold">Top-Performance-Anzeigen</span>
                <span className="text-xs text-gray-500">Basierend auf Lead-Generierung</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {error && (
                <div className="p-3 text-sm text-amber-700 bg-amber-50 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
                </div>
              ) : (
                topAds.length > 0 ? (
                  <div className="py-2">
                    {topAds.map((ad) => (
                      <AdCard key={ad.id} ad={ad} />
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    Keine Daten verfügbar
                  </div>
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

const AdCard: React.FC<{ ad: AdItem }> = ({ ad }) => {
  const performanceTrend = getPerformanceTrend(ad.performance);
  
  return (
    <div className="p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
      <div className="flex gap-3">
        {ad.imageUrl && (
          <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
            <img 
              src={ad.imageUrl} 
              alt={ad.name} 
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="flex-grow">
          <h4 className="font-medium text-sm truncate">{ad.name}</h4>
          <p className="text-xs text-gray-500 mb-1 truncate">{ad.headline}</p>
          
          <div className="flex justify-between text-xs">
            <span className={`flex items-center ${ad.status === 'Active' ? 'text-green-600' : 'text-gray-500'}`}>
              {ad.status}
            </span>
            <span className="text-gray-500">
              Leads: {ad.performance.leads || 0}
            </span>
          </div>
          
          <div className="mt-1 flex justify-between items-center">
            <div>
              <span className="text-xs font-medium">
                {(ad.performance.cpl || 0).toFixed(2)}€ CPL
              </span>
            </div>
            {performanceTrend && (
              <span className={`text-xs flex items-center ${
                performanceTrend.isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {performanceTrend.changePercent}% 
                {performanceTrend.trend === 'up' ? '↑' : '↓'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
